import type { FC, SVGProps } from 'react'
import { useLayoutEffect, useRef } from 'react'

import * as ToastPrimitive from '@radix-ui/react-toast'

import type { ToastProps } from '@/components/ui/toast'
import { useInternalContext } from '@/components/ui/toast'
import { classNames } from '@/utils/helpers'

import ErrorIcon from '../../icons/toast-error.svg'
import SuccessIcon from '../../icons/toast-success.svg'

const IconsMap: Partial<
  Record<ToastProps['toast']['status'], FC<SVGProps<SVGElement>>>
> = {
  success: SuccessIcon,
  error: ErrorIcon,
}

export const PusherToast = ({
  onOpenChange,
  toast,
  id,
  ...props
}: ToastProps) => {
  const ref = useRef<HTMLLIElement | null>(null)
  const { sortToasts, toastElementsMapRef } = useInternalContext()
  const toastElementsMap = toastElementsMapRef.current

  useLayoutEffect(() => {
    if (ref.current) {
      toastElementsMap.set(id, ref.current)
      sortToasts()
    }
  }, [id, sortToasts, toastElementsMap])

  const swipeEventListener = (event: ToastPrimitive.SwipeEvent) => {
    if (toast.closingDisabled) {
      event.preventDefault()
    }
  }

  const Icon = IconsMap[toast.status]

  return (
    <ToastPrimitive.Root
      {...props}
      ref={ref}
      type={toast.type}
      duration={toast.duration}
      className="ToastRoot"
      onOpenChange={onOpenChange}
      onSwipeStart={swipeEventListener}
      onSwipeMove={swipeEventListener}
      onSwipeEnd={swipeEventListener}
      onSwipeCancel={swipeEventListener}
    >
      <div
        className={classNames(
          'flex items-center gap-[18px] rounded-lg bg-card p-3 shadow-lg',
          {
            'bg-[#18321d]': toast.status === 'success',
            'bg-[#311817]': toast.status === 'error',
          },
        )}
        data-status={toast.type}
      >
        {Icon && <Icon />}
        <div className="col-2 flex flex-1 flex-col">
          <ToastPrimitive.Title className="w-auto font-medium text-white">
            {toast.title}
          </ToastPrimitive.Title>
          <div>
            <ToastPrimitive.Description className="line-clamp-2 block w-full text-xs font-medium text-white/80">
              {toast.description}
            </ToastPrimitive.Description>
          </div>
        </div>
        {!toast.closingDisabled && (
          <ToastPrimitive.Close
            aria-label="Close"
            className="px-1 text-white/40"
          >
            &#10006;
          </ToastPrimitive.Close>
        )}
        {toast.actionText && toast.action && (
          <ToastPrimitive.Action
            altText={toast.actionText}
            className="focus-visible:ring-ring ml-auto rounded-md bg-card px-2 py-2 text-sm font-medium transition-colors hover:bg-primary-50/80 focus-visible:outline-none focus-visible:ring-1 dark:hover:bg-secondary-600"
            onClick={toast.action}
          >
            {toast.actionText}
          </ToastPrimitive.Action>
        )}
      </div>
    </ToastPrimitive.Root>
  )
}
